// @ts-check

import { liteClient } from 'algoliasearch/lite'
import config from 'config'
import { locale as regionDefaultLocale } from '../region/locale'

/** @typedef {import("@sellpy/commons").marketplace.ExternalLocale} ExternalLocale */

/** @param {ExternalLocale} locale */
const getItemTypeIndex = (locale = regionDefaultLocale) =>
  ({
    sv: config.algolia.typeIndexSV,
    de: config.algolia.typeIndexDE,
    en: config.algolia.typeIndexEN,
    nl: config.algolia.typeIndexNL,
    da: config.algolia.typeIndexDA,
    pl: config.algolia.typeIndexPL,
    fi: config.algolia.typeIndexFI,
    fr: config.algolia.typeIndexFR,
    cs: config.algolia.typeIndexCS
  }[locale])

/** @param {ExternalLocale} locale */
export const getAlgoliaLocaleApplication = (locale = regionDefaultLocale) => ({
  client: algoliaLocaleClients[locale],
  searchSingleIndex: searchSingleIndexFunctions[locale],
  marketItemIndexes: ALGOLIA_SORT_INDICES[locale],
  querySuggestionIndexes: ALGOLIA_QUERY_SUGGESTION_INDEXES[locale]
})

export const getAlgoliaNonItemApplication = () => ({
  client: algoliaNonItemClient,
  searchSingleIndex: searchSingleIndexNonItem,
  brandIndex: config.algolia.brandIndex,
  modelIndex: config.algolia.modelIndex,
  getItemTypeIndex
})

const algoliaLocaleClients = {
  sv: liteClient(config.algolia.applicationIdSV, config.algolia.searchKeySV),
  nl: liteClient(config.algolia.applicationIdNL, config.algolia.searchKeyNL),
  da: liteClient(config.algolia.applicationIdDA, config.algolia.searchKeyDA),
  fi: liteClient(config.algolia.applicationIdFI, config.algolia.searchKeyFI),
  fr: liteClient(config.algolia.applicationIdFR, config.algolia.searchKeyFR),
  de: liteClient(config.algolia.applicationIdDE, config.algolia.searchKeyDE),
  pl: liteClient(config.algolia.applicationIdPL, config.algolia.searchKeyPL),
  cs: liteClient(config.algolia.applicationIdCS, config.algolia.searchKeyCS),
  en: liteClient(config.algolia.applicationIdEN, config.algolia.searchKeyEN)
}

const algoliaNonItemClient = liteClient(
  config.algolia.applicationIdNonItem,
  config.algolia.searchKeyNonItem
)

const ALGOLIA_SORT_INDICES = {
  sv: {
    salesStartDescending: config.algolia.marketItemSeIndexSaleStartDescending,
    relevance: config.algolia.marketItemSeIndexRelevance,
    cheapest: config.algolia.marketItemSeIndexPriceAscending,
    mostExpensive: config.algolia.marketItemSeIndexPriceDescending
  },
  de: {
    salesStartDescending: config.algolia.marketItemDeIndexSaleStartDescending,
    relevance: config.algolia.marketItemDeIndexRelevance,
    cheapest: config.algolia.marketItemDeIndexPriceAscending,
    mostExpensive: config.algolia.marketItemDeIndexPriceDescending
  },
  en: {
    salesStartDescending: config.algolia.marketItemEnIndexSaleStartDescending,
    relevance: config.algolia.marketItemEnIndexRelevance,
    cheapest: config.algolia.marketItemEnIndexPriceAscending,
    mostExpensive: config.algolia.marketItemEnIndexPriceDescending
  },
  nl: {
    salesStartDescending: config.algolia.marketItemNlIndexSaleStartDescending,
    relevance: config.algolia.marketItemNlIndexRelevance,
    cheapest: config.algolia.marketItemNlIndexPriceAscending,
    mostExpensive: config.algolia.marketItemNlIndexPriceDescending
  },
  da: {
    salesStartDescending: config.algolia.marketItemDaIndexSaleStartDescending,
    relevance: config.algolia.marketItemDaIndexRelevance,
    cheapest: config.algolia.marketItemDaIndexPriceAscending,
    mostExpensive: config.algolia.marketItemDaIndexPriceDescending
  },
  pl: {
    salesStartDescending: config.algolia.marketItemPlIndexSaleStartDescending,
    relevance: config.algolia.marketItemPlIndexRelevance,
    cheapest: config.algolia.marketItemPlIndexPriceAscending,
    mostExpensive: config.algolia.marketItemPlIndexPriceDescending
  },
  fi: {
    salesStartDescending: config.algolia.marketItemFiIndexSaleStartDescending,
    relevance: config.algolia.marketItemFiIndexRelevance,
    cheapest: config.algolia.marketItemFiIndexPriceAscending,
    mostExpensive: config.algolia.marketItemFiIndexPriceDescending
  },
  fr: {
    salesStartDescending: config.algolia.marketItemFrIndexSaleStartDescending,
    relevance: config.algolia.marketItemFrIndexRelevance,
    cheapest: config.algolia.marketItemFrIndexPriceAscending,
    mostExpensive: config.algolia.marketItemFrIndexPriceDescending
  },
  cs: {
    salesStartDescending: config.algolia.marketItemCsIndexSaleStartDescending,
    relevance: config.algolia.marketItemCsIndexRelevance,
    cheapest: config.algolia.marketItemCsIndexPriceAscending,
    mostExpensive: config.algolia.marketItemCsIndexPriceDescending
  }
}

/**
 * @type {Record<ExternalLocale, { querySuggestions: string, structuredSearch: string }>}
 */
const ALGOLIA_QUERY_SUGGESTION_INDEXES = {
  sv: {
    querySuggestions: config.algolia.querySuggestionsSV,
    structuredSearch: config.algolia.structuredSearchQuerySuggestionsSV
  },
  de: {
    querySuggestions: config.algolia.querySuggestionsDE,
    structuredSearch: config.algolia.structuredSearchQuerySuggestionsDE
  },
  en: {
    querySuggestions: config.algolia.querySuggestionsEN,
    structuredSearch: config.algolia.structuredSearchQuerySuggestionsEN
  },
  nl: {
    querySuggestions: config.algolia.querySuggestionsNL,
    structuredSearch: config.algolia.structuredSearchQuerySuggestionsNL
  },
  da: {
    querySuggestions: config.algolia.querySuggestionsDA,
    structuredSearch: config.algolia.structuredSearchQuerySuggestionsDA
  },
  pl: {
    querySuggestions: config.algolia.querySuggestionsPL,
    structuredSearch: config.algolia.structuredSearchQuerySuggestionsPL
  },
  fi: {
    querySuggestions: config.algolia.querySuggestionsFI,
    structuredSearch: config.algolia.structuredSearchQuerySuggestionsFI
  },
  fr: {
    querySuggestions: config.algolia.querySuggestionsFR,
    structuredSearch: config.algolia.structuredSearchQuerySuggestionsFR
  },
  cs: {
    querySuggestions: config.algolia.querySuggestionsCS,
    structuredSearch: config.algolia.structuredSearchQuerySuggestionsCS
  }
}

/** @param {import('algoliasearch/lite').LiteClient} client */
const makeSearchSingleIndex =
  (client) =>
  /**
   *
   * Emulates the `searchSingleIndex` method from the full algoliasearch client.
   * This allows us to keep using the lite client while still having the same interface as in other repos.
   *
   * @param {import('algoliasearch').SearchSingleIndexProps} params
   * @param {Parameters<import('algoliasearch/lite').LiteClient["search"]>[1]} requestOptions
   */
  (params, requestOptions) =>
    client
      .search(
        { requests: [{ indexName: params.indexName, ...params.searchParams }] },
        requestOptions
      )
      .then((res) => res.results[0])

// Only creating these once for stable references
const searchSingleIndexFunctions = {
  sv: makeSearchSingleIndex(algoliaLocaleClients.sv),
  de: makeSearchSingleIndex(algoliaLocaleClients.de),
  en: makeSearchSingleIndex(algoliaLocaleClients.en),
  nl: makeSearchSingleIndex(algoliaLocaleClients.nl),
  da: makeSearchSingleIndex(algoliaLocaleClients.da),
  pl: makeSearchSingleIndex(algoliaLocaleClients.pl),
  fi: makeSearchSingleIndex(algoliaLocaleClients.fi),
  fr: makeSearchSingleIndex(algoliaLocaleClients.fr),
  cs: makeSearchSingleIndex(algoliaLocaleClients.cs)
}

const searchSingleIndexNonItem = makeSearchSingleIndex(algoliaNonItemClient)
